<template>
	<v-navigation-drawer
		v-model="isOpen"
		location="left"
		temporary
		width="500"
		class="lead-edit-modal"
		data-cy="lead-edit-modal"
	>
		<v-card flat height="100%" class="d-flex flex-column">
			<v-card-title class="d-flex flex-column align-start pa-4 flex-shrink-0">
				<div class="d-flex align-center justify-space-between w-100">
					<span class="text-h6">Edit Lead</span>
					<v-btn icon variant="text" size="small" @click="close" data-cy="close-lead-edit-btn">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</div>
				<div
					v-if="editedLead.trial_id != null || editedLead.created_tstamp"
					class="text-caption text-medium-emphasis mt-1"
				>
					<span v-if="editedLead.trial_id != null" data-cy="lead-id-header">
						Lead #{{ editedLead.trial_id }}
					</span>
					<template v-if="editedLead.trial_id != null && editedLead.created_tstamp">
						<span> · </span>
					</template>
					<span v-if="editedLead.created_tstamp" data-cy="lead-created-date-header">
						Created {{ editedLead.created_tstamp }}
					</span>
				</div>
			</v-card-title>

			<v-divider class="flex-shrink-0" />

			<v-card-text class="pa-4 flex-grow-1" style="overflow-y: auto">
				<v-form ref="form" v-model="valid" lazy-validation>
					<v-row>
						<v-col cols="12">
							<v-text-field
								v-model="editedLead.school_name"
								label="School Name*"
								:rules="[rules.required]"
								required
								data-cy="lead-school-name"
							/>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="editedLead.district_name"
								label="District Name*"
								:rules="[rules.required]"
								required
								data-cy="lead-district-name"
							/>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="editedLead.address1"
								label="Address 1"
								data-cy="lead-address1"
							/>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="editedLead.address2"
								label="Address 2"
								data-cy="lead-address2"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field v-model="editedLead.city" label="City" data-cy="lead-city" />
						</v-col>

						<v-col cols="6">
							<v-text-field v-model="editedLead.state" label="State" data-cy="lead-state" />
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedLead.zipcode"
								label="Zipcode"
								data-cy="lead-zipcode"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedLead.country"
								label="Country"
								data-cy="lead-country"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedLead.firstname"
								label="First Name*"
								:rules="[rules.required]"
								required
								data-cy="lead-first-name"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedLead.lastname"
								label="Last Name*"
								:rules="[rules.required]"
								required
								data-cy="lead-last-name"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedLead.email"
								label="Email*"
								:rules="[rules.required, rules.email]"
								required
								data-cy="lead-email"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedLead.phone_number"
								label="Phone Number*"
								:rules="[rules.required]"
								required
								data-cy="lead-phone-number"
							/>
						</v-col>

						<v-col cols="6">
							<v-select
								v-model="editedLead.timezone"
								label="Timezone"
								:items="timezones"
								clearable
								data-cy="lead-timezone"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model.number="editedLead.num_students"
								label="Student Enrollment"
								type="number"
								:rules="[rules.number]"
								data-cy="lead-student-enrollment"
							/>
						</v-col>
					</v-row>
				</v-form>
			</v-card-text>

			<v-divider class="flex-shrink-0" />

			<v-card-actions class="pa-4 flex-shrink-0" style="min-height: 80px">
				<v-spacer />
				<v-btn
					color="grey"
					variant="outlined"
					@click="close"
					:disabled="loading"
					data-cy="cancel-lead-edit-btn"
				>
					Cancel
				</v-btn>

				<v-btn
					color="primary"
					variant="flat"
					@click="saveLead"
					:loading="loading"
					:disabled="!valid"
					data-cy="save-lead-details-btn"
				>
					Save Details
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-navigation-drawer>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useAlertsStore } from "@/stores/alerts";
import useLeads from "@/stores/support/leads";
import type { TrialMembership } from "@/stores/support/trials";
import dayjs from "dayjs";

const props = defineProps<{
  modelValue: boolean;
  lead: TrialMembership | null;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const leadsStore = useLeads();
const alertStore = useAlertsStore();

const form = ref();
const valid = ref(false);
const loading = ref(false);

const editedLead = ref<Partial<TrialMembership>>({});

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const timezones = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const rules = {
  required: (value: any) => !!value || "Field is required",
  email: (value: string) => /.+@.+/.test(value) || "Invalid email address",
  number: (value: any) =>
    !value || (!isNaN(parseFloat(value)) && isFinite(value)) || "Must be a number",
};

watch(
  () => props.lead,
  (newLead) => {
    if (newLead) {
      editedLead.value = { ...newLead };
      if (editedLead.value.created_tstamp) {
        editedLead.value.created_tstamp = dayjs(editedLead.value.created_tstamp).format(
          "MM/DD/YYYY HH:mm"
        );
      }
    }
  },
  { immediate: true }
);

const close = () => {
  isOpen.value = false;
  setTimeout(() => {
    editedLead.value = {};
    if (form.value) {
      form.value.resetValidation();
    }
  }, 300);
};

const saveLead = async () => {
  if (!form.value) return;

  const { valid: isValid } = await form.value.validate();
  if (!isValid || !props.lead) return;

  loading.value = true;

	try {
		const updateData: Record<string, unknown> = {
			school_name: editedLead.value.school_name,
			district_name: editedLead.value.district_name,
			address1: editedLead.value.address1,
			address2: editedLead.value.address2,
			city: editedLead.value.city,
			state: editedLead.value.state,
			zipcode: editedLead.value.zipcode,
			country: editedLead.value.country,
			firstname: editedLead.value.firstname,
			lastname: editedLead.value.lastname,
			email: editedLead.value.email,
			phone_number: editedLead.value.phone_number,
			num_students: editedLead.value.num_students,
		};

		// Only include timezone when set — empty string fails API `sometimes|required`
		if (editedLead.value.timezone) {
			updateData.timezone = editedLead.value.timezone;
		}

		const result = await leadsStore.updateLead(props.lead.trial_id, updateData);

    if (result.status) {
      alertStore.addAlert("success", "Lead updated successfully", 5);
      close();
    } else {
      alertStore.addAlert("error", result.message || "Failed to update lead", 10);
    }
  } catch (error) {
    console.error("Error updating lead:", error);
    alertStore.addAlert("error", "An error occurred while updating the lead", 10);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped lang="scss">
.lead-edit-modal {
	.v-card-title {
		background-color: #f5f5f5;
		border-bottom: 1px solid #e0e0e0;
	}

	.v-card-actions {
		background-color: #f5f5f5;
		border-top: 1px solid #e0e0e0;
	}
}
</style>
